
import { logger } from "@/services/LoggingService";
import { getCurrentOrigin, isPreviewEnvironment } from "@/constants/environments";

export type Provider = 'google' | 'azure' | 'linkedin_oidc';

export type ProviderConfig = {
  redirectTo: string;
  queryParams?: Record<string, string>;
  scopes?: string;
};

export type ProviderError = {
  code: string;
  message: string;
  provider?: Provider;
};

// Provider-specific configurations
const PROVIDER_CONFIGS: Record<Provider, Omit<ProviderConfig, 'redirectTo'>> = {
  google: {
    queryParams: {
      access_type: 'offline',
      prompt: 'consent',
    },
    scopes: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile'
  },
  azure: {
    queryParams: {
      prompt: 'consent'
    },
    scopes: 'openid email profile offline_access'
  },
  linkedin_oidc: {
    queryParams: {
      prompt: 'consent',
      response_mode: 'query'
    },
    scopes: 'openid profile email'
  }
};

// Error message mappings
const PROVIDER_ERROR_MESSAGES: Record<string, string> = {
  'azure.invalid_resource': 'Microsoft authentication configuration error. Please try again or contact support.',
  'azure.consent_required': 'Additional permissions are required. Please try again.',
  'google.access_denied': 'Google sign in was cancelled or denied.',
  'linkedin_oidc.access_denied': 'LinkedIn sign in was cancelled or denied.',
  'default': 'Authentication failed. Please try again.'
};

export const getProviderConfig = (provider: Provider): ProviderConfig => {
  const currentOrigin = getCurrentOrigin();
  
  // Store preview context for OAuth if needed
  if (isPreviewEnvironment()) {
    localStorage.setItem('auth_preview_context', currentOrigin);
    logger.debug('auth.provider', 'Stored preview context', { 
      origin: currentOrigin,
      provider,
      timestamp: new Date().toISOString()
    });
  }
  
  // Configure redirect URL
  const redirectUrl = `${currentOrigin}/auth`;
  
  logger.debug('auth.provider', 'Configuring OAuth', {
    provider,
    currentOrigin,
    redirectUrl,
    timestamp: new Date().toISOString()
  });

  return {
    redirectTo: redirectUrl,
    ...PROVIDER_CONFIGS[provider]
  };
};

export const getProviderErrorMessage = (error: any, provider: Provider): string => {
  const errorKey = `${provider}.${error.code || 'unknown'}`;
  return PROVIDER_ERROR_MESSAGES[errorKey] || PROVIDER_ERROR_MESSAGES.default;
};

export const handleProviderError = (error: any, provider: Provider): ProviderError => {
  logger.error('auth.provider', `${provider} auth failed`, {
    provider,
    error: error.message,
    code: error.code,
    status: error.status,
    timestamp: new Date().toISOString()
  });
  
  return {
    code: error.code || 'unknown',
    message: getProviderErrorMessage(error, provider),
    provider
  };
};
